import { Heightfield } from '../heightfield'
import { RAD, clamp01, smoothstep } from '../../core/math'

/**
 * Step 4 of the pipeline (spec §5): the hazard pass.
 *
 * "Terrain traps (gully bottoms, flat runouts under steep walls), no-fall zones
 * above cliff bands, glide cracks, exposed rock. All must be visible from
 * inspection. Nothing kills a daily-comparison game faster than a hazard you
 * couldn't have known about."
 *
 * Both fields are derived from the surface itself, which is what makes them
 * honest: exposure exists because there *is* a cliff below you, and a trap
 * exists because the ground genuinely flattens under a steep wall. Anything the
 * player can see, they can read.
 *
 * `exposure` then feeds the commitment term of the Flow score (spec §4: "A 45°
 * slope with a clean runout is worth less than a 42° slope above a cliff").
 */

const LOOK_AHEAD = 95
/**
 * Where a slide stops being recoverable.
 *
 * Set at 42° this flagged the entire mountain: a freeride face *is* mostly
 * 38-45°, so "exposed" has to mean cliff, not steep. Exposure is the axis the
 * spec leans on to tell lines apart — "a 45° slope with a clean runout is worth
 * less than a 42° slope above a cliff" — and it cannot do that job if it reads
 * 0.4 everywhere.
 */
const NO_FALL_DEG = 56

export function computeHazards(hf: Heightfield): void {
  // Slope and downhill direction are needed at every step of every march, in
  // both passes. Deriving them on the fly costs four bilinear height samples
  // apiece; precomputing once turns the whole pass from the most expensive
  // stage in the pipeline into a rounding error.
  const { cols, rows, cell, height } = hf
  const n = cols * rows
  const slopeDeg = new Float32Array(n)
  const dirX = new Float32Array(n)
  const dirZ = new Float32Array(n)

  for (let iz = 0; iz < rows; iz++) {
    for (let ix = 0; ix < cols; ix++) {
      const i = iz * cols + ix
      const l = height[iz * cols + Math.max(ix - 1, 0)]
      const r = height[iz * cols + Math.min(ix + 1, cols - 1)]
      const d = height[Math.max(iz - 1, 0) * cols + ix]
      const u = height[Math.min(iz + 1, rows - 1) * cols + ix]
      const dhdx = (r - l) / (2 * cell)
      const dhdz = (u - d) / (2 * cell)
      slopeDeg[i] = Math.atan(Math.hypot(dhdx, dhdz)) * RAD
      // Downhill direction is the negated gradient.
      const len = Math.hypot(dhdx, dhdz) || 1
      dirX[i] = -dhdx / len
      dirZ[i] = -dhdz / len
    }
  }

  // Traps first: a trap below you is a consequence, so exposure reads it.
  computeTraps(hf, slopeDeg, dirX, dirZ)
  computeExposure(hf, slopeDeg, dirX, dirZ)
}

/** Nearest-cell index, or -1 when out of bounds. */
function cellAt(hf: Heightfield, x: number, z: number): number {
  const ix = Math.round((x - hf.minX) / hf.cell)
  const iz = Math.round((z - hf.minZ) / hf.cell)
  if (ix < 0 || ix >= hf.cols || iz < 0 || iz >= hf.rows) return -1
  return iz * hf.cols + ix
}

function computeExposure(
  hf: Heightfield, slopeDeg: Float32Array, dirX: Float32Array, dirZ: Float32Array,
): void {
  const { cols, rows, cell } = hf
  const steps = Math.ceil(LOOK_AHEAD / cell)

  for (let iz = 0; iz < rows; iz++) {
    for (let ix = 0; ix < cols; ix++) {
      const i = iz * cols + ix
      let x = hf.xAt(ix)
      let z = hf.zAt(iz)
      let worst = 0

      // March down the fall line looking for what a slide would meet.
      //
      // Note there is deliberately no "total elevation lost" term. An earlier
      // version flagged anything that dropped more than 22m over the march,
      // which on a 40° face is every cell on the mountain — a 95m march loses
      // 80m by definition. Consequence is about what you hit, not how far you
      // travel, so it comes from the steepness of the ground below and from
      // whether a trap is waiting at the bottom.
      for (let s = 0; s < steps; s++) {
        const c = cellAt(hf, x, z)
        if (c < 0) break
        x += dirX[c] * cell
        z += dirZ[c] * cell
        const c2 = cellAt(hf, x, z)
        if (c2 < 0) break

        // Consequence decays with distance: a cliff 20m below you is the
        // problem, one 90m below is somebody else's.
        const proximity = 1 - s / steps
        const cliff = smoothstep(NO_FALL_DEG - 8, NO_FALL_DEG + 10, slopeDeg[c2])
        const trap = hf.trap[c2] * 0.7
        worst = Math.max(worst, Math.max(cliff, trap) * proximity)
      }
      hf.exposure[i] = clamp01(worst)
    }
  }
}

function computeTraps(
  hf: Heightfield, slopeDeg: Float32Array, dirX: Float32Array, dirZ: Float32Array,
): void {
  const { cols, rows, cell } = hf
  const steps = Math.ceil(70 / cell)

  for (let iz = 0; iz < rows; iz++) {
    for (let ix = 0; ix < cols; ix++) {
      const i = iz * cols + ix

      // A trap is somewhere you stop. Steep ground is never a trap.
      const flatness = 1 - smoothstep(16, 30, slopeDeg[i])
      if (flatness <= 0.01) { hf.trap[i] = 0; continue }

      // Gully bottom: strongly concave, collecting from both sides.
      const gully = clamp01(hf.concavity[i] * 9)

      // Flat ground directly under a steep wall — march *uphill*.
      let steepAbove = 0
      let ux = hf.xAt(ix)
      let uz = hf.zAt(iz)
      for (let s = 0; s < steps; s++) {
        const c = cellAt(hf, ux, uz)
        if (c < 0) break
        ux -= dirX[c] * cell
        uz -= dirZ[c] * cell
        const c2 = cellAt(hf, ux, uz)
        if (c2 < 0) break
        steepAbove = Math.max(steepAbove, smoothstep(36, 48, slopeDeg[c2]) * (1 - s / steps))
      }

      hf.trap[i] = clamp01(flatness * Math.max(gully * 0.75, steepAbove))
    }
  }
}

/**
 * Audit for the generator's own use, and for the debug overlay: a face that is
 * 40% no-fall zone is not a puzzle, it is a punishment.
 */
export function hazardSummary(hf: Heightfield): {
  exposedPct: number
  trapPct: number
  rockPct: number
} {
  let exposed = 0
  let trap = 0
  let rock = 0
  let n = 0
  for (let i = 0; i < hf.exposure.length; i += 3) {
    if (hf.exposure[i] > 0.5) exposed++
    if (hf.trap[i] > 0.4) trap++
    if (hf.rock[i] > 0.4) rock++
    n++
  }
  return {
    exposedPct: (exposed / n) * 100,
    trapPct: (trap / n) * 100,
    rockPct: (rock / n) * 100,
  }
}
